import React, { useState, useEffect, useCallback } from 'react';
import { listCollections, getCollectionDetails, deleteAllDocuments } from '../../services/adminApi';
import DocumentUploader from '../components/DocumentUploader';
import CollectionBrowser from '../components/CollectionBrowser';
import { notify, confirmAsync } from '../../utils/notify';
import './Settings.css';
import './QdrantManagement.css';

function QdrantManagement() {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [collectionDetails, setCollectionDetails] = useState(null);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const response = await listCollections();
      setCollections(response.data);
      if (response.data.length > 0 && !selectedCollection) {
        setSelectedCollection(response.data[0].name);
      }
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  };

  const handleCollectionSelect = (collectionName) => {
    setSelectedCollection(collectionName);
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    loadCollections();
    if (selectedCollection) {
      loadCollectionDetails();
    }
  };

  const loadCollectionDetails = useCallback(async () => {
    if (!selectedCollection) return;
    try {
      const response = await getCollectionDetails(selectedCollection);
      setCollectionDetails(response.data);
    } catch (err) {
      console.error('Failed to load collection details:', err);
    }
  }, [selectedCollection]);

  useEffect(() => {
    if (selectedCollection) {
      loadCollectionDetails();
    }
  }, [selectedCollection, loadCollectionDetails]);

  const handleDeleteAll = async () => {
    if (!selectedCollection) return;
    const documentCount = collectionDetails?.document_count || 0;
    if (documentCount === 0) {
      notify.info('No documents to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ALL ${documentCount} document(s) from "${selectedCollection}"?\n\nThis will:\n- Delete all documents from the database\n- Delete all vectors from Qdrant\n\nThis action CANNOT be undone!`;

    if (!(await confirmAsync({ message: confirmMessage, danger: true }))) {
      return;
    }

    try {
      await deleteAllDocuments(selectedCollection);
      loadCollections();
      loadCollectionDetails();
      notify.success(`Successfully deleted all ${documentCount} document(s) from the collection.`);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete all documents';
      notify.error('Failed to delete all documents: ' + errorMessage);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="settings-page">
      <h2>Knowledge Base Management</h2>
      
      <div className="settings-section">
        <div className="qdrant-header-actions">
          <button className="upload-button" onClick={() => setShowUpload(true)}>
            + Upload Document
          </button>
          {selectedCollection && (
            <button
              className="delete-all-button"
              onClick={handleDeleteAll}
              disabled={!collectionDetails || collectionDetails.document_count === 0}
              title="Delete all documents in this collection"
            >
              Delete All Documents
            </button>
          )}
        </div>

        <CollectionBrowser
          selectedCollection={selectedCollection}
          onCollectionSelect={handleCollectionSelect}
        />
      </div>

      {showUpload && (
        <DocumentUploader
          collectionName={selectedCollection}
          collections={collections}
          onUploadSuccess={handleUploadSuccess}
          onCancel={() => setShowUpload(false)}
        />
      )}
    </div>
  );
}

export default QdrantManagement;
